import { Box, InputBaseProps, TextFieldProps } from '@mui/material'
import { DateValidationError, DesktopDatePickerProps, PickerChangeHandlerContext } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import isNull from 'lodash/isNull'
import { FormViewerFieldMetadata } from '@omnea-app/fe-form-viewer/types'
import { getDateFnsLocaleObject } from '@omnea-app/fe-i18n/getDateFnsLocaleObject'
import { useTranslationsContext } from '@omnea-app/fe-i18n/TranslationsContext'
import { datePickerFormatByStandard } from '@omnea-app/packages/universal/date-utils/dateFormat'
import { toUTCDateMidnight } from '@omnea-app/packages/universal/date-utils/toUTCDateMidnight'
import { formatDateForPicker } from './helpers/formatDateForPicker'

export interface DatePickerProps extends Omit<DesktopDatePickerProps<Date>, 'value' | 'renderInput' | 'format'> {
  value: Date | null
  disabled?: boolean
  fullWidth?: boolean
  textFieldProps?: TextFieldProps
  inputProps?: InputBaseProps
  fieldMetadata?: FormViewerFieldMetadata
}

export const DatePicker = ({
  value,
  onChange,
  disabled,
  fullWidth,
  textFieldProps,
  inputProps,
  slotProps,
  slots,
  fieldMetadata,
  ...otherProps
}: DatePickerProps) => {
  const { locale, datePickerFormat } = useTranslationsContext()

  const { muiPickerFormat, placeholder } = datePickerFormatByStandard[datePickerFormat]

  const handleChange = (date: Date | null, context: PickerChangeHandlerContext<DateValidationError>) => {
    if (!onChange) return
    // Malformed dates clear the field
    // Other invalid dates (min/max) are handled by validation elsewhere
    if (isNull(date) || context.validationError === 'invalidDate') {
      onChange(null, context)
      return
    }
    // Remove timezone offset: Pass only the date as UTC midnight to save it as, e.g. 2024-10-28T00:00:00+00:00
    const dateInUTCMidnight = toUTCDateMidnight(date)
    onChange(dateInUTCMidnight, context)
  }

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={getDateFnsLocaleObject(locale)}
      localeText={{
        fieldMonthPlaceholder: (params) => (params.contentType === 'letter' ? 'MMM' : 'MM'),
      }}
    >
      <Box
        width={fullWidth ? '100%' : 'fit-content'}
        onClick={(event) => {
          event.stopPropagation()
          event.preventDefault()
        }}
      >
        <MUIDatePicker
          disabled={disabled}
          format={muiPickerFormat}
          value={formatDateForPicker(value)} // Pass UTC date converted to local date to display as e.g. 28/10/2024
          onChange={handleChange}
          slots={slots}
          slotProps={{
            ...slotProps,
            textField: {
              ...slotProps?.textField,
              placeholder,
              'data-is-ai-prefilled': fieldMetadata?.isAiPrefilled,
              ...textFieldProps,
              sx: [
                { width: fullWidth ? '100%' : 'fit-content' },
                ...(Array.isArray(textFieldProps?.sx) ? textFieldProps.sx : textFieldProps?.sx ? [textFieldProps.sx] : []),
              ],
              ...(inputProps ? { InputProps: inputProps } : {}),
            },
          }}
          {...otherProps}
        />
      </Box>
    </LocalizationProvider>
  )
}
